import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { FileXls, FilePdf, CheckCircle, WarningCircle, XCircle, Receipt, Bank, ClockCounterClockwise } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";
import LegrandReceiptsPanel from "../components/LegrandReceiptsPanel";

const _MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtD = (v) => { const m = String(v || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[3]}-${_MON[+m[2] - 1]}-${m[1].slice(2)}` : (v || "—"); };
const num = (v) => (Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (v, ccy) => `${ccy ? ccy + " " : ""}${num(v)}`;

const th = { padding: "9px 10px", fontSize: 11, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", textAlign: "left" };
const td = { padding: "8px 10px", fontSize: 12.5, borderBottom: "1px solid #F1F5F9", whiteSpace: "nowrap" };
const lbl = { fontSize: 11, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 5 };

const StatCard = ({ label, value, sub, color, testid }) => (
  <div data-testid={testid} style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: "14px 18px", minWidth: 160 }}>
    <div style={lbl}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: color || "#0F172A", fontVariantNumeric: "tabular-nums" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{sub}</div>}
  </div>
);

export default function ReceiptsFromAdvice() {
  const [mode, setMode] = useState("walmart");   // walmart | legrand
  const [financing, setFinancing] = useState(null);
  const [bankAdvice, setBankAdvice] = useState(null);
  const [customerCode, setCustomerCode] = useState("");
  const [parsed, setParsed] = useState(null);   // full /parse response
  const [rows, setRows] = useState([]);         // preview rows w/ selected flag
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [result, setResult] = useState(null);   // {results, posted, already_recorded, failed}
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/fa-logistics/receipts-from-advice/history");
      setHistory(data.history || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const parse = async () => {
    if (!financing || !bankAdvice) { toast.error("Choose both the financing advice (XLSX) and the bank advice (PDF)"); return; }
    setLoading(true); setResult(null); setParsed(null); setRows([]);
    try {
      const fd = new FormData();
      fd.append("customer_code", customerCode || "");
      fd.append("financing", financing);
      fd.append("bank_advice", bankAdvice);
      const { data } = await api.post("/fa-logistics/receipts-from-advice/parse", fd);
      setParsed(data);
      setRows((data.preview || []).map((p, i) => ({ ...p, _i: i, selected: p.postable })));
      const matched = (data.preview || []).length, nm = (data.not_matched || []).length;
      toast.success(`Matched ${matched} invoice(s)${nm ? ` · ${nm} unmatched` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not parse the advice files");
    } finally { setLoading(false); }
  };

  const toggle = (i) => setRows((rs) => rs.map((r) => (r._i === i ? { ...r, selected: !r.selected } : r)));
  const selectable = rows.filter((r) => r.postable);
  const allSel = selectable.length > 0 && selectable.every((r) => r.selected);
  const toggleAll = () => setRows((rs) => rs.map((r) => (r.postable ? { ...r, selected: !allSel } : r)));

  const selRows = rows.filter((r) => r.selected && r.postable);
  const selReceived = selRows.reduce((a, r) => a + (Number(r.from_customer) || 0), 0);
  const selCharges = selRows.reduce((a, r) => a + (Number(r.bank_charges) || 0), 0);
  const ccy = rows[0]?.currency || parsed?.bank?.currency || "";

  // ── Auto-adjust: net the difference (Total From Customer − Bank Advice amount) as an "Adjustment"
  // (other charges) against the LARGEST selected invoice, so the batch reconciles to the bank credit.
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const ADJ_LIMIT = 100;                         // only auto-adjust a small residual (≤ US$100)
  const bankAmt = round2(parsed?.bank?.amount);
  const diff = selRows.length && bankAmt > 0 ? round2(selReceived - bankAmt) : 0;  // + => we credit more than received
  const adjTarget = selRows.length
    ? selRows.reduce((best, r) => ((Number(r.from_customer) || 0) > (Number(best?.from_customer) || 0) ? r : best), null)
    : null;
  const adjEligible = diff > 0.009 && diff <= ADJ_LIMIT && !!adjTarget;   // small over-credit → net off
  const adjTooBig = diff > ADJ_LIMIT;                                     // exceeds limit → review
  const adjOver = diff < -0.009;                                         // bank paid MORE → review

  const post = async (dryRun) => {
    if (!selRows.length) { toast.error("Select at least one matched invoice"); return; }
    if (adjTooBig) {
      toast.error(`Difference ${money(diff, ccy)} exceeds the ${money(ADJ_LIMIT, ccy)} auto-adjust limit — review before posting`);
      return;
    }
    if (adjOver) {
      toast.error(`Bank credited ${money(-diff, ccy)} MORE than the customer total — please review (auto-adjust disabled)`);
      return;
    }
    const postRows = adjEligible
      ? selRows.map((r) => (r._i === adjTarget._i ? { ...r, adjustment: diff } : r))
      : selRows;
    if (!dryRun) {
      const msg = adjEligible
        ? `Post ${selRows.length} receipt voucher(s)?\n\n` +
          `From Customer: ${money(selReceived, ccy)}\nBank Charges: ${money(selCharges, ccy)}\n` +
          `Bank advice: ${money(bankAmt, ccy)}\n\n` +
          `A difference of ${money(diff, ccy)} will be netted as an ADJUSTMENT (other charges) against ` +
          `invoice #${adjTarget.invoice_no} (PO ${adjTarget.po_number}) so the batch reconciles to the bank advice.\n\nProceed?`
        : `Post ${selRows.length} receipt voucher(s) to the ERP?\n\n` +
          `From Customer: ${money(selReceived, ccy)}\nBank Charges: ${money(selCharges, ccy)}\n` +
          `Bank ref: ${parsed?.advice_ref || "—"}\n\nThis calls the SAME posting as Record Receipts and is idempotent (re-posting the same advice is safe).`;
      if (!window.confirm(msg)) return;
    }
    setPosting(true); setResult(null);
    try {
      const { data } = await api.post("/fa-logistics/receipts-from-advice/post", {
        rows: postRows,
        advice_ref: parsed?.advice_ref || "",
        source_files: parsed?.source_files || {},
        dry_run: !!dryRun,
      });
      setResult({ ...data, dry_run: !!dryRun });
      if (dryRun) toast.success(`Dry-run OK — ${data.posted} voucher(s) would post`);
      else {
        toast.success(`Posted ${data.posted} · ${data.already_recorded} already recorded · ${data.failed} failed`);
        loadHistory();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Posting failed");
    } finally { setPosting(false); }
  };

  const resById = {};
  (result?.results || []).forEach((x) => { resById[x.sale_no] = x; });

  const bank = parsed?.bank;

  return (
    <Layout>
      <div style={{ maxWidth: 1800, margin: "0 auto", padding: "0 4px" }} data-testid="rfa-page">
        <div style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: 0 }}>Receipts from Advice</h1>
          <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>
            Match customer remittance advices to bank credits. <b>Walmart</b> uses a financing advice (XLSX) + bank advice (PDF)
            and posts receipts; <b>Legrand</b> uses the buyer advice (PDF) + HDFC bank advices (PDF) and shows a verify table.
          </p>
        </div>

        {/* Mode tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }} data-testid="rfa-mode-tabs">
          <button data-testid="rfa-mode-walmart" onClick={() => setMode("walmart")} className="jde-btn"
            style={{ fontWeight: 700, border: "1px solid #CBD5E1", background: mode === "walmart" ? "#0F172A" : "#fff", color: mode === "walmart" ? "#fff" : "#334155" }}>
            Walmart (Financing + Bank)
          </button>
          <button data-testid="rfa-mode-legrand" onClick={() => setMode("legrand")} className="jde-btn"
            style={{ fontWeight: 700, border: "1px solid #CBD5E1", background: mode === "legrand" ? "#0F172A" : "#fff", color: mode === "legrand" ? "#fff" : "#334155" }}>
            Legrand (Buyer + HDFC)
          </button>
        </div>

        {mode === "legrand" ? <LegrandReceiptsPanel /> : (<>
        {/* Upload controls */}
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div>
            <div style={lbl}>Financing Advice (.xlsx)</div>
            <label data-testid="rfa-xlsx-label" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px", border: "1px dashed #94A3B8", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#334155", background: "#F8FAFC", maxWidth: 260 }}>
              <FileXls size={18} weight="fill" color="#16A34A" />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{financing ? financing.name : "Choose .xlsx…"}</span>
              <input data-testid="rfa-xlsx-input" type="file" accept=".xlsx" onChange={(e) => { setFinancing(e.target.files?.[0] || null); setResult(null); }} style={{ display: "none" }} />
            </label>
          </div>
          <div>
            <div style={lbl}>Bank Credit Advice (.pdf)</div>
            <label data-testid="rfa-pdf-label" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px", border: "1px dashed #94A3B8", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#334155", background: "#F8FAFC", maxWidth: 260 }}>
              <FilePdf size={18} weight="fill" color="#DC2626" />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{bankAdvice ? bankAdvice.name : "Choose .pdf…"}</span>
              <input data-testid="rfa-pdf-input" type="file" accept=".pdf" onChange={(e) => { setBankAdvice(e.target.files?.[0] || null); setResult(null); }} style={{ display: "none" }} />
            </label>
          </div>
          <div>
            <div style={lbl}>Customer Code (optional)</div>
            <input data-testid="rfa-customer-input" value={customerCode} onChange={(e) => setCustomerCode(e.target.value)} placeholder="e.g. WMRT"
              style={{ padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, width: 140 }} />
          </div>
          <div style={{ flex: 1 }} />
          <button data-testid="rfa-parse-btn" onClick={parse} disabled={loading || posting || !financing || !bankAdvice}
            className="jde-btn jde-btn-primary" style={{ opacity: (!financing || !bankAdvice) ? 0.5 : 1 }}>
            {loading ? "Reading & matching…" : "Parse & match"}
          </button>
        </div>

        {/* Bank advice summary + batch totals */}
        {parsed && (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            <div data-testid="rfa-bank-card" style={{ background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 12, padding: "14px 18px", minWidth: 320, flex: "1 1 320px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
                <Bank size={18} weight="fill" color="#0369A1" />
                <span style={{ fontSize: 13, fontWeight: 800, color: "#0C4A6E" }}>Bank Credit Advice</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 20px", fontSize: 12.5, color: "#0C4A6E" }}>
                <div>FX rate: <b>{num(bank?.fx_rate)}</b> ({bank?.currency_pair || "—"})</div>
                <div>Value date: <b>{fmtD(bank?.value_date)}</b></div>
                <div>Amount: <b>{money(bank?.amount, bank?.currency)}</b></div>
                <div>INR: <b>{bank?.inr_amount != null ? "₹" + num(bank.inr_amount) : "—"}</b></div>
                <div style={{ gridColumn: "1 / span 2" }}>Bank ref: <b>{parsed.advice_ref || "—"}</b></div>
              </div>
            </div>
            <StatCard testid="rfa-total-received" label="Total From Customer" value={money(selReceived, ccy)} sub={`${selRows.length} selected`} color="#166534" />
            <StatCard testid="rfa-total-charges" label="Total Bank Charges" value={money(selCharges, ccy)} sub="interest / fees" color="#B45309" />
            <StatCard testid="rfa-vouchers" label="Vouchers to Post" value={selRows.length} sub={`of ${rows.length} matched`} color="#0F172A" />
          </div>
        )}

        {/* Reconciliation banner: bank advice vs customer-receipt total */}
        {parsed && selRows.length > 0 && Math.abs(diff) > 0.009 && (
          <div data-testid="rfa-adjust-banner" style={{
            marginBottom: 14, padding: "11px 16px", borderRadius: 10, fontSize: 13,
            border: `1px solid ${adjEligible ? "#BBF7D0" : "#FDE68A"}`,
            background: adjEligible ? "#F0FDF4" : "#FFFBEB",
            color: adjEligible ? "#166534" : "#92400E",
          }}>
            {adjEligible && (
              <span>
                <b>Auto-adjust:</b> the {money(diff, ccy)} difference between Total From Customer
                ({money(selReceived, ccy)}) and the bank advice ({money(bankAmt, ccy)}) will be posted as an
                <b> Adjustment (other charges)</b> against invoice <b>#{adjTarget.invoice_no}</b> (PO {adjTarget.po_number}),
                so the batch reconciles exactly to the bank advice. You'll confirm on Post.
              </span>
            )}
            {adjTooBig && (
              <span><b>Review needed:</b> the difference {money(diff, ccy)} exceeds the {money(ADJ_LIMIT, ccy)} auto-adjust
                limit. Posting is blocked until the selection/advice is reviewed.</span>
            )}
            {adjOver && (
              <span><b>Review needed:</b> the bank credited {money(-diff, ccy)} MORE than the customer total
                ({money(selReceived, ccy)}). Auto-adjust is disabled for over-credits — please review.</span>
            )}
          </div>
        )}

        {/* Post actions */}
        {parsed && rows.length > 0 && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
            <button data-testid="rfa-dryrun-btn" onClick={() => post(true)} disabled={posting || !selRows.length}
              className="jde-btn" style={{ border: "1px solid #CBD5E1", background: "#fff", fontWeight: 700, opacity: !selRows.length ? 0.5 : 1 }}>
              Dry run
            </button>
            <button data-testid="rfa-post-btn" onClick={() => post(false)} disabled={posting || !selRows.length}
              className="jde-btn jde-btn-primary" style={{ opacity: !selRows.length ? 0.5 : 1 }}>
              {posting ? "Posting…" : `Post selected (${selRows.length})`}
            </button>
            {posting && (
              <span data-testid="rfa-posting-hint" style={{ fontSize: 12.5, color: "#64748B", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 13, height: 13, border: "2px solid #CBD5E1", borderTopColor: "#0EA5E9", borderRadius: "50%", display: "inline-block", animation: "rfa-spin .7s linear infinite" }} />
                Posting {selRows.length} voucher(s) to the ERP — this can take up to a minute, please wait…
              </span>
            )}
            <style>{"@keyframes rfa-spin{to{transform:rotate(360deg)}}"}</style>
          </div>
        )}

        {/* Post result */}
        {result && (
          <div data-testid="rfa-result" style={{ background: result.failed ? "#FEF2F2" : "#ECFDF5", border: `1px solid ${result.failed ? "#FECACA" : "#A7F3D0"}`, borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 13, color: result.failed ? "#991B1B" : "#065F46" }}>
            {result.dry_run ? <b>Dry run — nothing was written. </b> : null}
            <b>Posted {result.posted}</b> · already recorded {result.already_recorded} · failed {result.failed}
          </div>
        )}

        {/* Preview table */}
        {loading && <div style={{ padding: 30, textAlign: "center", color: "#64748B" }}>Reading the XLSX, extracting the PDF, and matching invoices…</div>}

        {parsed && !loading && rows.length > 0 && (
          <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto", marginBottom: 16 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="rfa-preview-table">
              <thead>
                <tr style={{ background: "#0F172A" }}>
                  <th style={{ ...th, width: 34 }}><input data-testid="rfa-select-all" type="checkbox" checked={allSel} onChange={toggleAll} /></th>
                  <th style={th}>PO</th>
                  <th style={th}>Export Inv</th>
                  <th style={th}>Invoice #</th>
                  <th style={th}>ERP Sale No</th>
                  <th style={th}>Party</th>
                  <th style={{ ...th, textAlign: "right" }}>Invoice Value</th>
                  <th style={{ ...th, textAlign: "right" }}>From Customer</th>
                  <th style={{ ...th, textAlign: "right" }}>Bank Charges</th>
                  <th style={{ ...th, textAlign: "right" }}>Rate</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const res = resById[r.sale_no];
                  return (
                    <tr key={r._i} data-testid={`rfa-row-${r._i}`} style={{ background: !r.postable ? "#FFF7ED" : "transparent" }}>
                      <td style={td}>
                        <input data-testid={`rfa-row-check-${r._i}`} type="checkbox" checked={!!r.selected} disabled={!r.postable} onChange={() => toggle(r._i)} />
                      </td>
                      <td style={{ ...td, fontWeight: 700 }}>{r.po_number}</td>
                      <td style={td}>{r.export_invoice_no || "—"}</td>
                      <td style={td}>{r.invoice_no || "—"}</td>
                      <td style={td}>{r.sale_no || "—"}</td>
                      <td style={{ ...td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{r.party || "—"}</td>
                      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(r.invoice_value, r.currency)}</td>
                      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#166534" }}>{num(r.from_customer)}</td>
                      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#B45309" }}>{num(r.bank_charges)}</td>
                      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{num(r.rate)}</td>
                      <td style={td}>
                        {res ? (
                          res.ok
                            ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: res.already_recorded ? "#B45309" : "#166534", fontWeight: 700, fontSize: 12 }}>
                                <CheckCircle size={14} weight="fill" /> {res.dry_run ? "Would post" : res.already_recorded ? "Already recorded" : `Posted ${res.voc_no || ""}`}
                              </span>
                            : <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#991B1B", fontWeight: 700, fontSize: 12 }}><XCircle size={14} weight="fill" /> {String(res.error || "failed").slice(0, 40)}</span>
                        ) : r.flags?.length
                          ? <span title={r.flags.join("; ")} style={{ display: "inline-flex", alignItems: "center", gap: 4, color: r.postable ? "#B45309" : "#991B1B", fontWeight: 700, fontSize: 12 }}><WarningCircle size={14} weight="fill" /> {r.flags.length} flag{r.flags.length > 1 ? "s" : ""}</span>
                          : <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#166534", fontWeight: 700, fontSize: 12 }}><CheckCircle size={14} weight="fill" /> Ready</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Not matched */}
        {parsed && (parsed.not_matched || []).length > 0 && (
          <div data-testid="rfa-unmatched" style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#991B1B", marginBottom: 8 }}>
              {parsed.not_matched.length} advice line(s) not matched
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ textAlign: "left" }}>
                <th style={{ fontSize: 11, fontWeight: 800, color: "#991B1B", padding: "4px 8px" }}>PO</th>
                <th style={{ fontSize: 11, fontWeight: 800, color: "#991B1B", padding: "4px 8px" }}>Supplier Doc No</th>
                <th style={{ fontSize: 11, fontWeight: 800, color: "#991B1B", padding: "4px 8px" }}>Amount</th>
                <th style={{ fontSize: 11, fontWeight: 800, color: "#991B1B", padding: "4px 8px" }}>Reason</th>
              </tr></thead>
              <tbody>
                {parsed.not_matched.map((n, i) => (
                  <tr key={i} data-testid={`rfa-unmatched-row-${i}`}>
                    <td style={{ fontSize: 12.5, padding: "4px 8px", fontWeight: 700 }}>{n.po_number}</td>
                    <td style={{ fontSize: 12.5, padding: "4px 8px" }}>{n.supplier_document_no}</td>
                    <td style={{ fontSize: 12.5, padding: "4px 8px" }}>{money(n.document_amount, n.document_currency)}</td>
                    <td style={{ fontSize: 12.5, padding: "4px 8px", color: "#991B1B" }}>{n.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!parsed && !loading && (
          <div style={{ padding: 40, textAlign: "center", color: "#94A3B8", background: "#fff", border: "1px dashed #E2E8F0", borderRadius: 12, marginBottom: 16 }}>
            <Receipt size={40} weight="thin" style={{ marginBottom: 8 }} />
            <div>Choose both files and press <b>Parse &amp; match</b> to preview the receipts before posting.</div>
          </div>
        )}

        {/* Upload history */}
        {history.length > 0 && (
          <div data-testid="rfa-history" style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "12px 14px", borderBottom: "1px solid #F1F5F9" }}>
              <ClockCounterClockwise size={17} color="#64748B" />
              <span style={{ fontSize: 13, fontWeight: 800, color: "#334155" }}>Recent uploads</span>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#F8FAFC" }}>
                <th style={{ ...th, color: "#475569" }}>Date</th>
                <th style={{ ...th, color: "#475569" }}>Customer</th>
                <th style={{ ...th, color: "#475569" }}>Advice Ref</th>
                <th style={{ ...th, color: "#475569" }}>Invoices Posted</th>
                <th style={{ ...th, color: "#475569" }}>Vouchers</th>
                <th style={{ ...th, color: "#475569" }}>User</th>
              </tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} data-testid={`rfa-history-row-${h.id}`}>
                    <td style={td}>{fmtD(h.date)}</td>
                    <td style={td}>{h.customer || "—"}</td>
                    <td style={td}>{h.advice_ref || "—"}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{h.invoices_posted}</td>
                    <td style={{ ...td, fontSize: 12, color: "#64748B" }}>{(h.voucher_numbers || []).join(", ") || "—"}</td>
                    <td style={td}>{h.user || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>)}
      </div>
    </Layout>
  );
}
